/* OBFUSCATED CODE - INTELLECTUAL PROPERTY PROTECTED */
'use client';
import { useEffect, useRef, useCallback } from 'react';
export function usePreviewFade({ previewCanvasRef, isPreviewMode }) {
    const animationFrameRef = useRef(null);
    const fadeStartTimeRef = useRef(null);
    const FADE_DURATION_MS = 500;
    const stopFade = useCallback(() => {
        if (animationFrameRef.current) {
            cancelAnimationFrame(animationFrameRef.current);
            animationFrameRef.current = null;
        }
        fadeStartTimeRef.current = null;
    }, []);
    const startFade = useCallback(() => {
        const canvas = previewCanvasRef?.current;
        if (!canvas) return;
        const ctx = canvas.getContext('2d');
        if (!ctx) return;
        stopFade();
        fadeStartTimeRef.current = performance.now();
        const snapshot = ctx.getImageData(0, 0, canvas.width, canvas.height);
        function fadeStep(now) {
            const elapsed = now - fadeStartTimeRef.current;
            const progress = Math.min(elapsed / FADE_DURATION_MS, 1);
            ctx.clearRect(0, 0, canvas.width, canvas.height);
            if (progress < 1) {
                const alpha = 1 - progress;
                ctx.globalAlpha = alpha;
                const tempCanvas = document.createElement('canvas');
                tempCanvas.width = canvas.width;
                tempCanvas.height = canvas.height;
                const tempCtx = tempCanvas.getContext('2d');
                tempCtx.putImageData(snapshot, 0, 0);
                ctx.drawImage(tempCanvas, 0, 0);
                ctx.globalAlpha = 1;
                animationFrameRef.current = requestAnimationFrame(fadeStep);
            } else {
                ctx.clearRect(0, 0, canvas.width, canvas.height);
                ctx.globalAlpha = 1;
                animationFrameRef.current = null;
            }
        }
        animationFrameRef.current = requestAnimationFrame(fadeStep);
    }, [previewCanvasRef, stopFade]);
    useEffect(() => {
        if (!isPreviewMode) {
            stopFade();
            return;
        }
        function handleDrawEnd() {
            startFade();
        }
        window.addEventListener('pointerup', handleDrawEnd);
        window.addEventListener('touchend', handleDrawEnd);
        return () => {
            window.removeEventListener('pointerup', handleDrawEnd);
            window.removeEventListener('touchend', handleDrawEnd);
        };
    }, [isPreviewMode, startFade, stopFade]);
    useEffect(() => {
        if (!isPreviewMode) {
            const canvas = previewCanvasRef?.current;
            if (canvas) {
                const ctx = canvas.getContext('2d');
                if (ctx) ctx.clearRect(0, 0, canvas.width, canvas.height);
            }
        }
    }, [isPreviewMode, previewCanvasRef]);
    return { startFade, stopFade };
}
